import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Input, Flex, Icon, Tooltip, Text, Wrap, WrapItem, Tag, TagLabel, TagCloseButton, useColorModeValue, Button } from "@chakra-ui/react";
import { MdClose } from "react-icons/md";
import { MainTable } from "../../../components/Table/MainTable";
import { useAuth0 } from "@auth0/auth0-react";
import { useKnoxHistory } from "../../../lib/hooks";
import moment from "moment";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { SidebarContext } from "../../../contexts/SidebarContext";
import FilterSystem, { getActiveFilterCount, getFilterDisplayValue, removeFilter as removeFilterHelper, clearAllFilters as clearAllFiltersHelper } from "../../../components/filters/FilterSystem";

function KnoxHistory() {
  const { user, isLoading: userLoading } = useAuth0();
  const { sidebarCollapsed } = useContext(SidebarContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);

  useEffect(() => {
    const token = localStorage.getItem("auth0_token");
    if (user && !userLoading && token) setMounted(true);
  }, [user, userLoading]);

  const { knoxHistory, isKnoxHistoryLoading } = useKnoxHistory(mounted);

  const allRows = Array.isArray(knoxHistory?.body) ? knoxHistory?.body : Array.isArray(knoxHistory) ? knoxHistory : [];

  const [filters, setFilters] = useState({});
  const [sortKey, setSortKey] = useState("createdDatetime");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");

  const eventTypes = useMemo(
    () =>
      Array.from(new Set((allRows || []).map((r) => r?.eventType).filter(Boolean))).sort(),
    [allRows]
  );
  const createdByList = useMemo(
    () =>
      Array.from(new Set((allRows || []).map((r) => r?.createdBy).filter(Boolean))).sort(),
    [allRows]
  );

  const filterFields = useMemo(
    () => [
      {
        key: "createdDatetime",
        label: "Created Date",
        type: "daterange",
      },
      {
        key: "eventType",
        label: "Event Type",
        type: "multiselect",
        options: eventTypes,
      },
      {
        key: "serialNumber",
        label: "Serial Number",
        type: "text",
        placeholder: "Enter serial number",
      },
      {
        key: "orderId",
        label: "Order ID",
        type: "number",
        placeholder: "Enter order ID",
      },
      {
        key: "message",
        label: "Message",
        type: "text",
        placeholder: "Search message",
      },
      {
        key: "createdBy",
        label: "Created By",
        type: "multiselect",
        options: createdByList,
      },
    ],
    [eventTypes, createdByList]
  );

  const filtered = useMemo(() => {
    const toStr = (v) => (v === null || v === undefined ? "" : String(v)).toLowerCase();
    return allRows.filter((r) => {
      const created = r?.createdDatetime ? moment.unix(r.createdDatetime).format("YYYY-MM-DD") : "";

      // Date range filter
      if (filters.createdDatetime_from || filters.createdDatetime_to) {
        const createdMoment = moment.unix(r.createdDatetime);
        if (filters.createdDatetime_from && createdMoment.isBefore(moment(filters.createdDatetime_from))) {
          return false;
        }
        if (filters.createdDatetime_to && createdMoment.isAfter(moment(filters.createdDatetime_to).endOf('day'))) {
          return false;
        }
      }

      // Event type filter (multiselect)
      if (filters.eventType && Array.isArray(filters.eventType) && filters.eventType.length > 0) {
        if (!filters.eventType.includes(r?.eventType)) {
          return false;
        }
      }

      // Serial number filter
      if (filters.serialNumber && !toStr(r?.serialNumber).includes(toStr(filters.serialNumber))) {
        return false;
      }

      // Order ID filter
      if (filters.orderId && !toStr(r?.orderId).includes(toStr(filters.orderId))) {
        return false;
      }

      // Message filter
      if (filters.message && !toStr(r?.message).includes(toStr(filters.message))) {
        return false;
      }

      // Created by filter (multiselect)
      if (filters.createdBy && Array.isArray(filters.createdBy) && filters.createdBy.length > 0) {
        if (!filters.createdBy.includes(r?.createdBy)) {
          return false;
        }
      }

      // Search filter
      if (search) {
        const hay = [created, r?.eventType, r?.serialNumber, r?.orderId, r?.message, r?.createdBy]
          .map((x) => toStr(x))
          .join(" ");
        return hay.includes(search.toLowerCase());
      }

      return true;
    });
  }, [allRows, filters, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const getVal = (row) => {
        if (sortKey === "createdDatetime") return row?.createdDatetime ? row.createdDatetime : 0;
        return (row?.[sortKey] ?? "").toString().toLowerCase();
      };
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return sortDir === "asc" ? va - vb : vb - va;
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalCount = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const pageSlice = useMemo(() => {
    const start = (page - 1) * perPage;
    return sorted.slice(start, start + perPage);
  }, [sorted, page, perPage]);

  const handleHeaderSort = (accessor) => {
    if (sortKey === accessor) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(accessor);
      setSortDir("asc");
    }
  };

  const columns = useMemo(
    () => [
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("createdDatetime")} cursor="pointer">Created {sortKey==="createdDatetime" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "createdDatetime" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("eventType")} cursor="pointer">Event Type {sortKey==="eventType" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "eventType" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("serialNumber")} cursor="pointer">Serial Number {sortKey==="serialNumber" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "serialNumber" },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("orderId")} cursor="pointer">Order ID {sortKey==="orderId" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "orderId" },
      {
        Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("message")} cursor="pointer">Message {sortKey==="message" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>),
        accessor: "message",
        Cell: ({ value }) => (
          <Tooltip label={value} placement="top" hasArrow>
            <Text
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              maxW="300px"
            >
              {value}
            </Text>
          </Tooltip>
        )
      },
      { Header: () => (<Flex align="center" onClick={()=>handleHeaderSort("createdBy")} cursor="pointer">Created By {sortKey==="createdBy" && (<Icon as={sortDir==="asc"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "createdBy" },
    ],
    [sortKey, sortDir]
  );

  const data = useMemo(() => {
    return pageSlice.map((d) => ({
      createdDatetime: d?.createdDatetime ? moment.unix(d.createdDatetime).format("YYYY-MM-DD HH:mm:ss") : "-",
      eventType: d?.eventType ?? "-",
      serialNumber: d?.serialNumber ?? "-",
      orderId: d?.orderId ?? "-",
      message: d?.message ?? "-",
      createdBy: d?.createdBy ?? "-",
    }));
  }, [pageSlice]);

  // Export rows: export all (unpaginated) or current? App convention: pass full or current.
  // We'll export all rows per spec (current filtered or all). No filters here, so all.
  const exportRows = useMemo(() => {
    return sorted.map((d) => ({
      createdDatetime: d?.createdDatetime ? moment.unix(d.createdDatetime).format("YYYY-MM-DD HH:mm:ss") : "",
      eventType: d?.eventType || "",
      serialNumber: d?.serialNumber || "",
      orderId: d?.orderId ?? "",
      message: d?.message || "",
      createdBy: d?.createdBy || "",
    }));
  }, [sorted]);

  return (
    <Box
      position="fixed"
      top="80px"
      left={{ base: 0, xl: sidebarCollapsed ? "80px" : "290px" }}
      right="0"
      bottom="0"
      display="flex"
      flexDirection="column"
      p={{ base: "20px", md: "30px" }}
      pe="20px"
      overflow="hidden"
    >
      <Box mb={3} flexShrink={0}>
        <Flex gap={3} alignItems="center" mb={3}>
          <Input
            placeholder="Search all columns"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            flex="1"
          />
          <FilterSystem
            filterFields={filterFields}
            onFilterChange={(newFilters) => {
              setFilters(newFilters);
              setPage(1);
            }}
            initialFilters={filters}
            externalFilters={filters}
            showTags={false}
          />
        </Flex>
        {getActiveFilterCount(filters) > 0 && (
          <Wrap spacing={2}>
            {filterFields.map((field) => {
              if (field.type === "daterange") {
                const fromValue = filters[`${field.key}_from`];
                const toValue = filters[`${field.key}_to`];
                if (fromValue || toValue) {
                  return (
                    <WrapItem key={field.key}>
                      <Tag
                        size="md"
                        borderRadius="full"
                        variant="solid"
                        bg={useColorModeValue("blue.50", "blue.900")}
                        color={useColorModeValue("blue.700", "blue.200")}
                      >
                        <TagLabel>
                          {field.label}: {fromValue || "..."} to {toValue || "..."}
                        </TagLabel>
                        <TagCloseButton onClick={() => removeFilterHelper(`${field.key}_from`, filters, setFilters, (newFilters) => {
                          setFilters(newFilters);
                          setPage(1);
                        }, filterFields)} />
                      </Tag>
                    </WrapItem>
                  );
                }
              } else {
                const value = filters[field.key];
                const hasValue = field.type === "multiselect"
                  ? Array.isArray(value) && value.length > 0
                  : value;

                if (hasValue) {
                  return (
                    <WrapItem key={field.key}>
                      <Tag
                        size="md"
                        borderRadius="full"
                        variant="solid"
                        bg={useColorModeValue("blue.50", "blue.900")}
                        color={useColorModeValue("blue.700", "blue.200")}
                      >
                        <TagLabel>
                          {field.label}: {getFilterDisplayValue(field, value)}
                        </TagLabel>
                        <TagCloseButton onClick={() => removeFilterHelper(field.key, filters, setFilters, (newFilters) => {
                          setFilters(newFilters);
                          setPage(1);
                        }, filterFields)} />
                      </Tag>
                    </WrapItem>
                  );
                }
              }
              return null;
            })}
          </Wrap>
        )}
      </Box>
      <Box flex="1" minH="0">
        <MainTable
          loaded={!isKnoxHistoryLoading}
          data={data}
          columns={columns}
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={page}
          perPage={perPage}
          perPageOptions={[50, 100, 200]}
          exports={exportRows}
          totalPage={totalPages}
          name={`Knox History (${totalCount})`}
          containerHeight="100%"
        />
      </Box>
    </Box>
  );
}

export default KnoxHistory;
